// SINGLE SOURCE OF TRUTH for a team card's status → border/pill colour.
// Pill tokens are reused from the dark-table badges so the card never drifts from
// the Teams table. The solid accent drives the 6px top border / 1px side borders
// + stat numbers:
//   invite-pending → HF/Secondary (#727CF5)   owned → BADGE_OWNED purple
//   invited/active → per-team accent (else Pink/500 #D53F8C default)
import colors from 'theme/colors.theme'
import { isValidHex, pickBrandColor } from 'constants/brand-palette'
import { BADGE_INVITED, BADGE_OWNED, BADGE_PENDING } from 'constants/dark-tokens'
import { AuthRoleIdEnum } from 'constants/user'
import {
  EMembershipStatus,
  getMembershipBadge,
  MembershipBadge,
  MEMBERSHIP_BADGE_LABEL
} from 'constants/membership'

type PillTheme = { color: string; bg: string; border: string }

const PILL: Record<MembershipBadge, PillTheme> = {
  owned: BADGE_OWNED,
  invited: BADGE_INVITED,
  'invite-pending': BADGE_PENDING
}

const ACCENT_PENDING = colors.secondary[500] // #727CF5 (HF/Secondary)
const ACCENT_OWNED = BADGE_OWNED.color // #C4A1FF
/** Design default border for invited/active cards when no per-team accent given. */
export const TEAM_CARD_ACCENT_DEFAULT = colors.pink[500] // #D53F8C

/**
 * Per-team accent (invited/active card borders + stat numbers).
 *
 * Prefers the Organization's own brand colour so a team card and its dashboard
 * banner always agree; falls back to a deterministic pick keyed by orgId, which
 * matches what the API seeds, so a team keeps one colour across renders and
 * across the two surfaces.
 *
 * Superseded the old six-colour local palette — one shared source now, otherwise
 * the same team shows one colour on its card and another on the banner.
 */
export const teamAccentColor = (orgId: number, primaryColor?: string): string =>
  isValidHex(primaryColor) ? primaryColor : pickBrandColor(orgId)

/** Card status = membership badge (owned | invited | invite-pending). One mapper. */
export const resolveTeamCardStatus = (input: {
  roleId?: AuthRoleIdEnum
  status?: EMembershipStatus
}): MembershipBadge => getMembershipBadge(input)

export interface TeamCardStatusTheme {
  /** Solid accent for the 1px border + stat numbers. */
  accent: string
  pill: PillTheme
  label: string
}

const resolveAccent = (status: MembershipBadge, accentColor?: string): string => {
  if (status === 'invite-pending') return ACCENT_PENDING
  if (status === 'owned') return ACCENT_OWNED
  return accentColor ?? TEAM_CARD_ACCENT_DEFAULT
}

/** Status → { accent, pill, label }. `accentColor` overrides the invited/active border. */
export const teamCardStatusTheme = (status: MembershipBadge, accentColor?: string): TeamCardStatusTheme => ({
  accent: resolveAccent(status, accentColor),
  pill: PILL[status],
  label: MEMBERSHIP_BADGE_LABEL[status]
})
